'use client';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { Box, BoxProps, Typography, type SxProps } from '@mui/material';
import React, { useState } from 'react';
import { sanitizeInstanceDomIdSegment } from '../utils/sanitizeDomIdSegment';

interface ShowMoreProps extends BoxProps {
  instanceId: string;
  isDefaultExpanded?: boolean;
  maxContentHeight?: number;
  showMoreText?: string;
  showLessText?: string;
  hideBottomGradient?: boolean;
}

const contentStyles: Record<string, string> = {
  overflowY: 'hidden',
  textAlign: 'inherit',
  color: 'inherit',
  position: 'relative',
};

export const ShowMore: React.FC<ShowMoreProps> = ({
  children,
  hideBottomGradient = false,
  instanceId,
  isDefaultExpanded = false,
  maxContentHeight = 150,
  showMoreText = 'Show More',
  showLessText = 'Show Less',
  ...boxProps
}) => {
  // isExpanded is used to set aria-expanded attribute only
  const [isExpanded, setIsExpanded] = useState(isDefaultExpanded);
  const [isShorterContent, setIsShorterContent] = useState(false);
  const safeId = sanitizeInstanceDomIdSegment(instanceId, 'show-more');
  const { sx: boxStyles, ...otherBoxProps } = boxProps;

  const showMoreStyles: SxProps = {
    fontWeight: 'bold',
    textTransform: 'uppercase',
    cursor: 'pointer',
    color: 'blues.main',
    alignItems: 'center',
    display: 'inline-flex',
    maxWidth: 'inherit',
    gap: 0.5,
    '&:hover': {
      textDecoration: 'underline',
      textDecorationColor: 'blues.main',
    },
  };

  const handleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsExpanded(e.target.checked);
  };

  return (
    <Box sx={boxStyles} {...otherBoxProps}>
      {isShorterContent && (
        // Adding exactly 34px to the bottom margin to reduce layout shift on
        // page render for the "Show More" button
        <Box
          id={`${safeId}-short-content`}
          sx={{
            mb: '34px',
            ...contentStyles,
          }}
        >
          {children}
        </Box>
      )}
      {!isShorterContent && (
        <Box
          sx={{
            position: 'relative',
            '& .checkbox-toggle:checked ~ .show-more-content': {
              maxHeight: '100%',
              '&::after': {
                display: 'none',
              },
            },
            '& .checkbox-toggle:checked ~ label .show-more': {
              display: 'none',
            },
            '& .checkbox-toggle:checked ~ label .show-less': {
              display: 'flex',
            },
          }}
        >
          <input
            type="checkbox"
            className="checkbox-toggle"
            id={`checkbox-toggle-${safeId}`}
            defaultChecked={isDefaultExpanded}
            hidden
            onChange={handleToggle}
          />
          <Box
            className="show-more-content"
            id={`show-more-content-${safeId}`}
            aria-expanded={isExpanded}
            role="region"
            sx={{
              ...contentStyles,
              marginBottom: 3,
              maxHeight: maxContentHeight,
              '&::after': {
                display: hideBottomGradient ? 'none' : 'block',
                position: 'absolute',
                width: '100%',
                background: 'linear-gradient(hsla(0,0%,100%,0),white)',
                bottom: 0,
                height: '50%',
                content: '""',
              },
            }}
            ref={(ref: HTMLDivElement) => {
              const scrollHeight = ref?.scrollHeight;
              if (scrollHeight && scrollHeight < maxContentHeight) {
                setIsShorterContent(true);
              }
            }}
          >
            {children}
          </Box>
          <Box
            component="label"
            htmlFor={`checkbox-toggle-${safeId}`}
            aria-controls={`show-more-content-${safeId}`}
            tabIndex={0}
            sx={{
              display: 'block',
              maxWidth: 'fit-content',
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                document?.getElementById(`checkbox-toggle-${safeId}`)?.click();
              }
            }}
          >
            <Typography
              className="show-more"
              component="span"
              variant="textSmall"
              sx={showMoreStyles}
            >
              {showMoreText}
              <KeyboardArrowDown />
            </Typography>
            <Typography
              className="show-less"
              component="span"
              variant="textSmall"
              sx={{ ...showMoreStyles, display: 'none' }}
            >
              {showLessText}
              <KeyboardArrowUp />
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};
